import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWhatsApp } from "@/lib/siteSettings";
import { SEO } from "@/lib/seo";

const Citizenship = () => {
  const { t } = useTranslation();
  const { buildWaUrl } = useWhatsApp();
  const waUrl = buildWaUrl(t("citizenship.cta.whatsappMessage"));

  const requirements = [1, 2, 3, 4].map((i) => t(`citizenship.requirements.${i}`));
  const steps = [1, 2, 3, 4].map((i) => ({
    n: `0${i}`,
    title: t(`citizenship.process.step${i}.title`),
    desc: t(`citizenship.process.step${i}.desc`),
  }));

  return (
    <div className="bg-background">
      <SEO title={t("seo.citizenship.title")} description={t("seo.citizenship.description")} path="/citizenship" />

      {/* HERO */}
      <section className="bg-hero-gradient px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-card/70 px-4 py-1.5 text-sm font-bold text-primary">
            🇹🇷 {t("citizenship.page.badge")}
          </div>
          <h1 className="text-balance text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            {t("citizenship.page.title")}
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">
            {t("citizenship.page.hero")}
          </p>
        </div>
      </section>

      {/* REQUIREMENTS */}
      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-6 text-2xl font-black sm:text-3xl">{t("citizenship.requirements.title")}</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {requirements.map((r, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-soft">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold leading-6">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-surface px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-8 text-2xl font-black sm:text-3xl">{t("citizenship.process.title")}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-card p-5 shadow-soft transition hover:-translate-y-1 hover:border-primary/60">
                <span className="text-xs font-black tracking-widest text-primary">{s.n}</span>
                <h3 className="mt-2 text-lg font-black">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-7">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-2xl border border-primary/40 bg-card p-8 text-center shadow-premium">
          <h2 className="text-2xl font-black">{t("citizenship.cta.title")}</h2>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a href={waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-black text-white shadow-premium hover:scale-[1.02] transition">
              <MessageCircle className="h-4 w-4" /> {t("footer.whatsapp")}
            </a>
            <Link to="/listings?citizenship=true" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-black text-primary-foreground hover:scale-[1.02] transition">
              {t("citizenship.cta.browse")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Citizenship;
